import { useMemo } from 'react';

import {
  selectSupportedPlatforms,
  withPinnedPlatformOption,
  type AgentRoutingSchema,
} from '../../../utils/get-agent-routing-schemas';
import { foldPlatformKey } from '../../../utils/platform-catalog';

/**
 * The three platform lists the Add/Edit modal needs, and the one distinction
 * between them that must never blur (PSTUDIO-4072).
 *
 * Imported from the module rather than the `utils` barrel on purpose: the
 * barrel re-exports `platform-logo`, which reads `logos` off the design system
 * and cannot be loaded in a test environment. Keeping this hook free of
 * design-system imports is what makes it renderable in jsdom — and the modal
 * that consumes it is not, which is why the decision lives here.
 *
 * - `offeredOptions` — what a user may PICK: Contact Center + `LAAPI_*`.
 * - `renderedPlatformOptions` — what the picker DRAWS: the offer, plus the
 *   platform an existing connection is already bound to (see
 *   `withPinnedPlatformOption`).
 * - `noPlatformsInEnv` — whether the picker the user is LOOKING AT is empty.
 *   Read off the rendered list, not the offer: in edit mode the pinned row keeps
 *   the picker usable, so an empty offer says nothing about the dead end this
 *   flag exists to explain. It lives here rather than in the modal because the
 *   modal cannot be rendered in a test at all (its design-system Lit elements
 *   crash the environment), so a predicate left inline there is pinned by
 *   nothing — which is exactly how it was first written against the wrong list.
 * - `selectedSchema` — resolved against the FULL catalog, never the rendered
 *   list. Two reasons, both load-bearing. It carries the platform's config
 *   schema, so a legacy connection's credential fields stay editable; and its
 *   ABSENCE is the signal the submit reads to trust the connection's stored
 *   routing (`external_agent` + its callback URL). Resolving it against the
 *   rendered list would answer that question with a pinned row that declares
 *   `isExternal: false`, so a plain re-save of an untouched external connection
 *   would write `agentRouting` as the integration name and drop the callback
 *   URL the backend requires.
 */
export const useLiveAgentPlatformOptions = ({
  catalog,
  isEdit,
  editingPlatformId,
  selectedRouting,
}: {
  /** The env's FULL catalog, as `buildPlatformCatalog` returned it. */
  catalog: readonly AgentRoutingSchema[];
  isEdit: boolean;
  /** `platformId` of the connection being edited, if any. */
  editingPlatformId?: string | null;
  /** The platform currently selected in the form. */
  selectedRouting: string;
}): {
  offeredOptions: readonly AgentRoutingSchema[];
  renderedPlatformOptions: readonly AgentRoutingSchema[];
  noPlatformsInEnv: boolean;
  selectedSchema: AgentRoutingSchema | undefined;
} => {
  const offeredOptions = useMemo(
    () => selectSupportedPlatforms(catalog),
    [catalog]
  );

  const renderedPlatformOptions = useMemo(
    () =>
      isEdit
        ? withPinnedPlatformOption(offeredOptions, catalog, editingPlatformId)
        : offeredOptions,
    [isEdit, offeredOptions, catalog, editingPlatformId]
  );

  const selectedSchema = useMemo(() => {
    const exact = catalog.find(
      (option) => option.agent_routing === selectedRouting
    );
    if (exact || !selectedRouting) {
      return exact;
    }

    // Fallback for a design-system defect, not for sloppy data: `ps-select-option`
    // rewrites a space in its `value` to `_` (`select-option.wc.ts`'s `@watch`),
    // so picking an integration whose name contains a space hands the form a key
    // the catalog does not contain. Without this the schema silently fails to
    // resolve, the credential fields never render, AND the submit takes the
    // "no schema" branch — which for a NEW connection writes the raw key as an
    // internal routing with no callback URL. Matching on the folded key keeps
    // the picker usable; the persisted `platformId` is still whatever the DS
    // handed us, so the defect itself is filed separately.
    //
    // `foldPlatformKey` is shared with the payload's `platformUnchanged` on
    // purpose — two answers to "is this the same platform?" that fold
    // differently is how a re-pick of the SAME row read as a switch.
    const folded = foldPlatformKey(selectedRouting);
    return catalog.find(
      (option) => foldPlatformKey(option.agent_routing) === folded
    );
  }, [catalog, selectedRouting]);

  return {
    offeredOptions,
    renderedPlatformOptions,
    noPlatformsInEnv: renderedPlatformOptions.length === 0,
    selectedSchema,
  };
};
